"use client";

import { useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Controller, useForm } from "react-hook-form";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { signIn } from "next-auth/react";
import { toast } from "@/lib/toast";
import { ChevronLeftIcon } from "@/icons";
import { register } from "@/hooks/api/client";
import {
  SignUpFormSchema,
  SignUpStep1Schema,
  type SignUpFormValues,
  REFERRAL_SOURCE_OPTIONS,
  VAT_SCHEME_OPTIONS,
} from "@/lib/definitions";
import { Button } from "@/components/ui/button/Button";
import { Card } from "@/components/ui/card/Card";
import { FormField } from "@/components/ui/form-field/FormField";
import { Input } from "@/components/ui/input/Input";
import { Select } from "@/components/ui/select/Select";
import { PasswordInput } from "@/components/ui/password-input/PasswordInput";
import { useSendOtpMutation } from "@/hooks/api/useUserProfile";
import { useEmailVerificationStore } from "@/store/emailVerificationStore";
import { useAvailableVatSchemesQuery } from "@/features/settings/hook";

type FormStep = 1 | 2;

function ApiErrorAlert({ message }: { message: string }) {
  return (
    <div className="mb-5 rounded-lg border border-error-200 bg-error-50 px-4 py-3 dark:border-error-500/20 dark:bg-error-500/10">
      <p className="text-sm font-medium text-error-600 dark:text-error-400">
        {message}
      </p>
    </div>
  );
}

export default function SignUpForm() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState<FormStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [passwordIsPwned, setPasswordIsPwned] = useState(false);
  const sendOtpMutation = useSendOtpMutation();

  const {
    data: apiVatSchemes,
    isLoading: isLoadingVatSchemes,
    isError: isErrorVatSchemes,
  } = useAvailableVatSchemesQuery();

  const vatSchemeOptions = useMemo(() => {
    // If error (e.g., 401 on signup) or no data, use fallback immediately
    if (isErrorVatSchemes || !apiVatSchemes || apiVatSchemes.length === 0) {
      return VAT_SCHEME_OPTIONS;
    }
    // If loading and no cached data yet, show loading state
    if (isLoadingVatSchemes) {
      return [{ value: "Standard", label: "Loading..." }];
    }
    return apiVatSchemes.map((scheme) => {
      let label = scheme;
      if (scheme === "Standard") label = "Standard rate (20%)";
      else if (scheme === "Flat Rate") label = "Flat Rate Scheme";
      else if (scheme === "Cash Accounting") label = "Cash Accounting Scheme";
      return { value: scheme, label };
    });
  }, [apiVatSchemes, isLoadingVatSchemes, isErrorVatSchemes]);

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    mode: "onChange",
    defaultValues: {
      fullName: "",
      username: "",
      password: "",
      email: undefined,
      businessName: "",
      businessEmail: "",
      businessPhoneNumber: "",
      vatScheme: "Standard",
      accountantEmail: "",
      referralSource: "",
      agreedToTerms: false,
    },
  });

  const handleContinueToStep2 = () => {
    clearErrors(["fullName", "username", "password", "referralSource"]);

    const step1Values = {
      fullName: getValues("fullName"),
      username: getValues("username"),
      password: getValues("password"),
      referralSource: getValues("referralSource"),
    };

    const result = SignUpStep1Schema.safeParse(step1Values);

    if (!result.success) {
      for (const issue of result.error.issues) {
        const fieldName = issue.path[0];
        if (typeof fieldName === "string") {
          setError(fieldName as keyof SignUpFormValues, {
            type: "manual",
            message: issue.message,
          });
        }
      }
      return;
    }

    setValue("agreedToTerms", true);
    setCurrentStep(2);
    setApiError(null);
  };

  const onSubmit = async (data: SignUpFormValues) => {
    // Block submission if password is pwned
    if (passwordIsPwned) {
      setApiError(
        "Please choose a different password that hasn't been exposed in data breaches.",
      );
      return;
    }

    setIsLoading(true);
    setApiError(null);

    // Validate all fields before submission, mapping businessEmail to email
    const validationResult = SignUpFormSchema.safeParse({
      ...data,
      email: data.businessEmail,
    });

    if (!validationResult.success) {
      for (const issue of validationResult.error.issues) {
        const fieldName = issue.path[0];
        if (typeof fieldName === "string") {
          setError(fieldName as keyof SignUpFormValues, {
            type: "manual",
            message: issue.message,
          });
        }
      }
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        fullName: data.fullName,
        username: data.username,
        password: data.password,
        businessName: data.businessName,
        email: data.businessEmail,
        businessEmail: data.businessEmail,
        businessPhoneNumber: data.businessPhoneNumber,
        vatScheme: data.vatScheme,
        accountantEmail: data.accountantEmail || undefined,
        referralSource: data.referralSource || undefined,
      };

      await register(payload);

      const signInResult = await signIn("credentials", {
        redirect: false,
        username: data.username,
        password: data.password,
      });

      if (signInResult?.error) {
        throw new Error(
          "Account created but sign in failed. Please sign in manually.",
        );
      }

      const { setVerificationData } = useEmailVerificationStore.getState();
      setVerificationData({
        email: data.businessEmail,
      });

      await sendOtpMutation.mutateAsync({
        channel: "EMAIL",
        email: data.businessEmail,
      });

      useEmailVerificationStore.getState().setLastOtpSentAt(Date.now());

      toast.success("Account created! An OTP has been sent to your email.");
      router.push("/verify-email");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create account. Please try again.";
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setApiError(null);
    } else {
      router.push("/");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-section">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
        <Card className="w-full max-w-120">
          {currentStep === 2 ? (
            <button
              type="button"
              onClick={handleBack}
              className="mb-6 inline-flex items-center text-sm text-text-secondary transition-colors hover:text-text-secondary dark:text-gray-400 dark:hover:text-gray-300"
            >
              <ChevronLeftIcon className="mr-1 h-4 w-4" />
              Back
            </button>
          ) : null}

          <Link href="/" className="mb-8 flex justify-center">
            <Image
              src="/images/logo/logo.webp"
              width={120}
              height={40}
              alt="Repayas"
              priority
              className="h-auto"
            />
          </Link>

          {currentStep === 1 ? (
            <>
              <div className="mb-8 text-center">
                <h1 className="mb-2 text-2xl font-medium text-text-primary dark:text-white/90 sm:text-3xl">
                  Create your account
                </h1>
                <p className="text-sm text-text-secondary dark:text-gray-400">
                  Know exactly what is safe to spend. Every day.
                </p>
              </div>

              {apiError ? <ApiErrorAlert message={apiError} /> : null}

              <form className="space-y-5" noValidate>
                <FormField
                  label="Full name"
                  htmlFor="fullName"
                  error={errors.fullName?.message}
                >
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="fullName"
                        type="text"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Your full name"
                        autoComplete="name"
                        disabled={isLoading}
                        error={!!errors.fullName}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label="Username"
                  htmlFor="signupUsername"
                  error={errors.username?.message}
                >
                  <Controller
                    name="username"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="signupUsername"
                        type="text"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Choose a username"
                        autoComplete="username"
                        disabled={isLoading}
                        error={!!errors.username}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label="Password"
                  htmlFor="signupPassword"
                  hint="Minimum 12 characters "
                >
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <PasswordInput
                        id="signupPassword"
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Choose a password"
                        autoComplete="new-password"
                        disabled={isLoading}
                        error={!!errors.password}
                        checkBreach={true}
                        onBreachCheckComplete={(isPwned) =>
                          setPasswordIsPwned(isPwned)
                        }
                      />
                    )}
                  />
                </FormField>

                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <p className="mb-3 text-xs text-text-secondary dark:text-gray-400">
                    Optional — helps us improve Repayas for you
                  </p>
                  <FormField
                    label="How did you hear about Repayas?"
                    htmlFor="referralSource"
                  >
                    <Controller
                      name="referralSource"
                      control={control}
                      render={({ field }) => (
                        <Select
                          id="referralSource"
                          {...field}
                          value={field.value ?? ""}
                          disabled={isLoading}
                          options={REFERRAL_SOURCE_OPTIONS.map((option) => ({
                            value: option,
                            label: option,
                          }))}
                        />
                      )}
                    />
                  </FormField>
                </div>

                <div className="pt-1">
                  <Button
                    type="button"
                    onClick={handleContinueToStep2}
                    disabled={isLoading}
                    fullWidth
                    variant="primary"
                    size="md"
                    rounded="lg"
                    className="h-12! text-base! text-white"
                  >
                    Continue
                  </Button>
                </div>
              </form>

              <p className="mt-6 text-center text-xs text-text-secondary">
                By creating an account you agree to our{" "}
                <Link
                  href="/terms"
                  className="text-text-primary hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  href="/privacy"
                  className="text-text-primary hover:underline"
                >
                  Privacy Policy
                </Link>
              </p>

              <p className="mt-6 text-center text-sm text-text-secondary">
                Already have an account?{" "}
                <Link
                  href="/signin"
                  className="text-text-primary hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="mb-8 text-center">
                <h1 className="mb-2 text-2xl font-medium text-text-primary dark:text-white/90 sm:text-3xl">
                  Tell us about your business
                </h1>
                <p className="text-sm text-text-secondary dark:text-gray-400">
                  We use this to set up your Safe-to-Spend calculation.
                </p>
              </div>

              {apiError ? <ApiErrorAlert message={apiError} /> : null}

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-5"
                noValidate
              >
                <FormField
                  label="Business name"
                  htmlFor="businessName"
                  error={errors.businessName?.message}
                >
                  <Controller
                    name="businessName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="businessName"
                        type="text"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="MyBusiness Ltd"
                        autoComplete="organization"
                        disabled={isLoading}
                        error={!!errors.businessName}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label="Business email"
                  htmlFor="businessEmail"
                  error={errors.businessEmail?.message}
                >
                  <Controller
                    name="businessEmail"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="businessEmail"
                        type="email"
                        {...field}
                        value={field.value ?? ""}
                        placeholder="accounts@my-business.co.uk"
                        autoComplete="email"
                        disabled={isLoading}
                        error={!!errors.businessEmail}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label="Business phone number"
                  htmlFor="businessPhoneNumber"
                  error={errors.businessPhoneNumber?.message}
                >
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value="+44"
                      disabled
                      readOnly
                      className="w-16 px-3 text-center"
                    />
                    <Controller
                      name="businessPhoneNumber"
                      control={control}
                      render={({ field }) => (
                        <div className="min-w-0 flex-1">
                          <Input
                            id="businessPhoneNumber"
                            type="tel"
                            {...field}
                            value={field.value ?? ""}
                            placeholder="7911 123 456"
                            autoComplete="tel"
                            disabled={isLoading}
                            error={!!errors.businessPhoneNumber}
                          />
                        </div>
                      )}
                    />
                  </div>
                </FormField>

                <FormField
                  label="VAT scheme"
                  htmlFor="vatScheme"
                  error={errors.vatScheme?.message}
                  hint={
                    !errors.vatScheme
                      ? "This helps us calculate your VAT liability accurately."
                      : undefined
                  }
                >
                  <Controller
                    name="vatScheme"
                    control={control}
                    render={({ field }) => (
                      <Select
                        id="vatScheme"
                        {...field}
                        value={field.value ?? "Standard"}
                        disabled={isLoading || isLoadingVatSchemes}
                        error={!!errors.vatScheme}
                        options={vatSchemeOptions}
                      />
                    )}
                  />
                </FormField>

                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <p className="mb-3 text-xs text-text-secondary dark:text-gray-400">
                    Optional — helps us improve Repayas for you
                  </p>
                  <FormField
                    label="Accountant email"
                    htmlFor="accountantEmail"
                    error={errors.accountantEmail?.message}
                    hint={
                      !errors.accountantEmail
                        ? "We can send them a summary link so they can verify your numbers."
                        : undefined
                    }
                  >
                    <Controller
                      name="accountantEmail"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="accountantEmail"
                          type="email"
                          {...field}
                          value={field.value ?? ""}
                          placeholder="your.accountant@firm.co.uk"
                          autoComplete="email"
                          disabled={isLoading}
                          error={!!errors.accountantEmail}
                        />
                      )}
                    />
                  </FormField>
                </div>

                <div className="pt-6">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    loading={isLoading}
                    fullWidth
                    variant="primary"
                    size="md"
                    rounded="lg"
                    className="h-12! text-base! text-white"
                  >
                    Create account
                  </Button>
                </div>
              </form>
            </>
          )}
        </Card>

        <p className="mt-6 flex max-w-md items-center justify-center gap-2 px-4 text-center text-xs text-text-secondary dark:text-gray-400">
          <ShieldCheckIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Your data is never shared or sold. Open Banking secured by
            TrueLayer.
          </span>
        </p>
      </div>

      <footer className="flex justify-center border-t border-gray-200 py-8 gap-6 px-4 pb-8 text-xs text-gray-400 dark:text-text-secondary">
        <Link
          href="/terms"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Terms
        </Link>
        <Link
          href="/privacy"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Privacy
        </Link>
        <Link
          href="/cookies"
          className="hover:text-text-secondary dark:hover:text-gray-300"
        >
          Cookies
        </Link>
      </footer>
    </div>
  );
}
